'use client'

import { useCallback, useEffect, useRef } from 'react'
import { InterviewEventType } from '@/constants/enums'
import type { InterviewEventMetadata } from '@/constants/enums'
import { getScreeningInterviewAuthPayload } from '@/utils/screening-interview-auth.util'

const INTERVIEW_EVENTS_ENDPOINT = '/api/interviews/screeningInterview/events'

interface InterviewEventPayload {
    type: string
    timestamp: string
    metadata?: InterviewEventMetadata
}

export function useInterviewEvents() {
    const eventsRef = useRef<InterviewEventPayload[]>([])
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const flushIntervalMs = 15 * 1000

    const flushEvents = useCallback(() => {
        const authPayload = getScreeningInterviewAuthPayload()
        if (!authPayload || eventsRef.current.length === 0) return

        const eventsToSend = eventsRef.current.splice(0)
        const payload = JSON.stringify({
            ...authPayload,
            events: eventsToSend,
        })

        if (typeof navigator.sendBeacon !== 'undefined') {
            navigator.sendBeacon(INTERVIEW_EVENTS_ENDPOINT, payload)
        } else {
            fetch(INTERVIEW_EVENTS_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
            }).catch(() => {
                /* ignore errors */
            })
        }
    }, [])

    const addInterviewEvent = useCallback((eventType: InterviewEventType | string, metadata?: InterviewEventMetadata) => {
        eventsRef.current.push({
            type: eventType,
            timestamp: new Date().toISOString(),
            ...(metadata && Object.keys(metadata).length > 0 ? { metadata } : {}),
        })
    }, [])

    useEffect(() => {
        window.addEventListener('beforeunload', flushEvents)
        timerRef.current = setInterval(flushEvents, flushIntervalMs)

        return () => {
            window.removeEventListener('beforeunload', flushEvents)
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
            flushEvents()
        }
    }, [flushEvents, flushIntervalMs])

    return { addInterviewEvent, flushInterviewEvents: flushEvents }
}
